/**
 * Générateur — Enchaîner des opérations.
 * Attendu ID : 902 | Niveau : 5ème | Domaine : Opérations (cycle 4 rénové, cluster 3)
 *
 * Plan de cartes (validé par le responsable pédagogique) :
 *
 *   Carte « effectuer » — Ex.9 Effectue les calculs
 *     Formats : texte (saisie du résultat — gabarits simples), texte_trou_saisie
 *     (étapes guidées — gabarits à crochets imbriqués).
 *     7 gabarits générés par construction (les structures du doc) :
 *       G1  a − (b + c)            G2  a + (x − y) × 10
 *       G3  a + b × c − d          G4  a − [b − (x + y)]
 *       G5  a − [b + c × 0,5]      G6  (a + b) × (c − d)
 *       G7  a − (x + y)  ← dixièmes, ajouté à la revue 2026-08-02
 *     Le format à trous est intégralement en dixièmes ; côté saisie, seul G7
 *     l'est (1 tirage sur 5), les six autres tombent juste — ce qui rend enfin
 *     vraie la consigne « en écriture décimale si besoin ».
 *     Intermédiaires POSITIFS et EXACTS par construction (on tire les composants
 *     et on pose l'expression, jamais l'inverse). Misconceptions : calcul de
 *     gauche à droite, parenthèses ignorées.
 *
 *   Carte « programme » — Créé n°5 Suites d'opérations
 *     Formats : texte_trou_saisie (une case par étape), qcm_unique (résultat).
 *     Programmes en français construits À REBOURS (le résultat final est tiré,
 *     on remonte pour que la division tombe juste). 2 gabarits : entier
 *     (× k, + p, ÷ d) et décimal (+ y, × 100, − q).
 *     Misconceptions : étape oubliée (s'arrête avant la division), résultat
 *     intermédiaire donné comme final.
 *
 *   Carte « etapes » — Créé n°6 Remettre les étapes dans l'ordre
 *     Format : ordonner. Gabarits en CHAÎNE STRICTE uniquement (chaque étape
 *     consomme le résultat de la précédente) — jamais de structure à branches
 *     indépendantes, qui rendrait deux ordres valides (🔴 indécidable).
 *     QUATRE chaînes : deux croissantes (a − (b + c × d), (a + b) × c − d) et
 *     deux que la division fait redescendre ((a − b) ÷ c + d, a − b × c ÷ d),
 *     ajoutées à la revue 2026-08-02 — sans elles, ranger les étiquettes du
 *     plus petit nombre au plus grand suffisait à réussir 92 % du temps (A2).
 *
 *   Carte « verifier » — Créé n°7 Le calcul de Léa (gardé en O4, arbitrage)
 *     Formats : tableau_vf, qcm_unique. B = (x − y) × k + p ÷ q.
 *     Le V/F porte UNIQUEMENT sur l'ordre des étapes — 4 affirmations tirées
 *     dans trois groupes (par quoi commencer, quelle contrainte d'ordre, quelle
 *     opération en dernier), toutes arithmétiquement justes, conformément à la
 *     consigne. Le groupe « résultat final » a été retiré (revue 2026-08-02) :
 *     il servait une ligne « B = <faux> » dans 53 % des tirages alors que la
 *     consigne annonce que tous les calculs sont justes — l'élève qui la croyait
 *     était compté faux (A1).
 *     Mix V/F garanti (au moins une vraie, au moins une fausse).
 *
 * Adaptations : les batteries a–f deviennent UN item par tirage (Duolingo).
 */

import {
    randomChoice, randInt, shuffle,
    makeQcm, makeSaisie, makeVraiFaux, makeTexteTrou, makeOrdonner,
} from '../../../utils';

// ─── Constantes ──────────────────────────────────────────────
const ATTENDU_ID = 902;

const L_EFF  = 'Effectuer un calcul (parenthèses et crochets)';
const L_PROG = 'Suivre un programme de calcul';
const L_ORD  = 'Remettre les étapes d\'un calcul dans l\'ordre';
const L_VER  = 'Vérifier les étapes d\'un calcul';

const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const LETTRES_CALC = ['A', 'B', 'C', 'D', 'E', 'F'];

/** Dixièmes → écriture FR. */
const dixToFr = (d) => (d % 10 === 0 ? String(d / 10) : `${Math.floor(d / 10)},${d % 10}`);

/** Déduplique des choix de QCM (rotation des misconceptions par mélange). */
const dedupeChoix = (bonne, candidats, complements) => {
    const norm = (s) => String(s).replace(',', '.').trim();
    const vus = new Set([norm(bonne)]);
    const retenus = [];
    [...shuffle(candidats), ...complements].forEach(c => {
        const n = norm(c);
        if (!vus.has(n) && retenus.length < 3) { vus.add(n); retenus.push(String(c)); }
    });
    return retenus;
};

// ═══════════════════════════════════════════════════════════
// Carte 1 — Effectuer un calcul
// ═══════════════════════════════════════════════════════════

/** Gabarits « résultat seul » — retournent { expr, resFr }. */
const GABARITS_SIMPLES = [
    () => {   // G1 : a − (b + c), entiers
        const b = randInt(8, 30), c = randInt(8, 30), res = randInt(10, 40);
        const a = b + c + res;
        return { expr: `${a} − (${b} + ${c})`, resFr: String(res) };
    },
    () => {   // G2 : a + (x − y) × 10 — x − y décimal propre, ×10 entier
        let d10 = randInt(11, 49);
        if (d10 % 10 === 0) d10 += randomChoice([1, 3, 7]);
        const y10 = randInt(3, 30);
        const a = randInt(2, 20);
        return {
            expr: `${a} + (${dixToFr(y10 + d10)} − ${dixToFr(y10)}) × 10`,
            resFr: String(a + d10),
        };
    },
    () => {   // G3 : a + b × c − d (priorités sans parenthèses)
        const b = randomChoice([12, 15, 25, 35]), c = randInt(3, 6);
        const a = randInt(10, 30), d = randInt(5, 29);
        return { expr: `${a} + ${b} × ${c} − ${d}`, resFr: String(a + b * c - d) };
    },
    () => {   // G6 : (a + b) × (c − d)
        const a = randInt(11, 45), b = randInt(5, 20);
        const c = randInt(9, 19), d = randInt(2, c - 3);
        return { expr: `(${a} + ${b}) × (${c} − ${d})`, resFr: String((a + b) * (c - d)) };
    },
    /**
     * G7 : a − (x + y), x et y au dixième — le SEUL gabarit à résultat décimal
     * (revue 2026-08-02). Les quatre précédents donnaient tous un entier, alors
     * que la consigne annonce « en écriture décimale si besoin » : la promesse
     * n'était jamais tenue (A7). Même misconception que G1 — parenthèse ignorée,
     * on ne soustrait que le premier terme.
     */
    () => {
        let s10 = randInt(31, 98);
        if (s10 % 10 === 0) s10 += randomChoice([1, 3, 7]);   // somme jamais entière
        const x10 = randInt(5, s10 - 5);
        const a = Math.ceil(s10 / 10) + randInt(2, 25);        // a entier > x + y
        return {
            expr: `${a} − (${dixToFr(x10)} + ${dixToFr(s10 - x10)})`,
            resFr: dixToFr(a * 10 - s10),
        };
    },
];

const genEffectuerSaisie = () => {
    const { expr, resFr } = randomChoice(GABARITS_SIMPLES)();
    const lettre = randomChoice(LETTRES_CALC);
    const texte = `<p>Effectue le calcul suivant.</p><p><strong>${lettre} = ${expr}</strong></p>` +
        '<p>Écris le résultat en écriture décimale si besoin (ex. : 12,5).</p>';
    return makeSaisie(resFr, texte, opts(L_EFF));
};

/**
 * Gabarits « étapes guidées » (crochets imbriqués) — convention de la FEUILLE
 * (retour du responsable pédagogique) : à chaque ligne on RÉÉCRIT toute
 * l'expression, le calcul prioritaire étant remplacé par son résultat :
 *   F = 16 − [10 − (1,1 + 3,6)]
 *   F = 16 − [10 − {{4,7}}]
 *   F = 16 − {{5,3}}
 *   F = {{10,7}}
 */
const GABARITS_ETAPES = [
    (lettre) => {   // G4 : a − [b − (x + y)] — décimaux 1 déc
        let s10 = randInt(21, 69);
        if (s10 % 10 === 0) s10 += randomChoice([1, 3, 9]);
        const x10 = randInt(3, s10 - 3);
        const y10 = s10 - x10;
        const b = Math.ceil(s10 / 10) + randInt(2, 9);          // b entier > x+y
        const inner10 = b * 10 - s10;                            // b − (x+y), dixièmes
        const a = Math.ceil(inner10 / 10) + randInt(5, 20);      // a entier > inner
        const res10 = a * 10 - inner10;
        const xF = dixToFr(x10), yF = dixToFr(y10), sF = dixToFr(s10);
        const iF = dixToFr(inner10), rF = dixToFr(res10);
        return {
            expr: `${a} − [${b} − (${xF} + ${yF})]`,
            phrase:
                `${lettre} = ${a} − [${b} − (${xF} + ${yF})]\n` +
                `${lettre} = ${a} − [${b} − {{${sF}}}]\n` +
                `${lettre} = ${a} − {{${iF}}}\n` +
                `${lettre} = {{${rF}}}`,
        };
    },
    (lettre) => {   // G5 : a − [b + c × 0,5]
        const c = randInt(2, 9);
        const c5x10 = c * 5;                                     // c × 0,5 en dixièmes
        const b = randInt(4, 15);
        const inner10 = b * 10 + c5x10;
        const a = Math.ceil(inner10 / 10) + randInt(3, 15);
        const res10 = a * 10 - inner10;
        const cF = dixToFr(c5x10), iF = dixToFr(inner10), rF = dixToFr(res10);
        return {
            expr: `${a} − [${b} + ${c} × 0,5]`,
            phrase:
                `${lettre} = ${a} − [${b} + ${c} × 0,5]\n` +
                `${lettre} = ${a} − [${b} + {{${cF}}}]\n` +
                `${lettre} = ${a} − {{${iF}}}\n` +
                `${lettre} = {{${rF}}}`,
        };
    },
];

const genEffectuerEtapesTrous = () => {
    const lettre = randomChoice(LETTRES_CALC);
    const { phrase } = randomChoice(GABARITS_ETAPES)(lettre);
    const texte = '<p>Effectue le calcul <strong>comme sur ta feuille</strong> : ' +
        'à chaque ligne, remplace le calcul prioritaire par son résultat.</p>' +
        '<p>Complète (écris les nombres sans zéro inutile, ex. : 12,5) :</p>';
    return makeTexteTrou(phrase, texte, { ...opts(L_EFF), mode: 'saisie' });
};

// ═══════════════════════════════════════════════════════════
// Carte 2 — Programme de calcul
// ═══════════════════════════════════════════════════════════

/**
 * Gabarit entier : × k, + p, ÷ d — construit à rebours (division exacte).
 *
 * Les bornes ont été élargies à la revue (2026-08-02) : le vivier ne comptait
 * que 765 quadruplets (n, k, d, f) admissibles, entièrement épuisés dès
 * quelques milliers de tirages, et PARTAGÉS avec le QCM de la même carte — d'où
 * le doublon intermittent du harnais sur 40 itérations. `n` et `k` n'ont pas
 * bougé : la multiplication de tête reste identique (n × k ≤ 108), seuls le
 * diviseur, le résultat final et l'écart p gagnent de la place. Vivier : 1782.
 */
const tirerProgrammeEntier = () => {
    for (let i = 0; i < 60; i++) {
        const n = randInt(3, 12), k = randInt(3, 9);
        const d = randomChoice([2, 4, 5, 10]);
        const f = randInt(3, 20);
        const p = f * d - n * k;
        if (p >= 1 && p <= 50) return { n, k, p, d, nk: n * k, v: f * d, f };
    }
    return { n: 6, k: 7, p: 8, d: 10, nk: 42, v: 50, f: 5 };     // repli : l'exercice source
};

/** Gabarit décimal : + y, × 100, − q — exact par construction (dixièmes). */
const tirerProgrammeDecimal = () => {
    let x10 = randInt(12, 59), y10 = randInt(11, 48);
    if ((x10 + y10) % 10 === 0) x10 += randomChoice([1, 3]);
    const q = randInt(5, 40);
    const s10 = x10 + y10;
    return { x10, y10, q, s10, v: s10 * 10, f: s10 * 10 - q };   // s×100 = s10×10 (entier)
};

const genProgrammeTrous = () => {
    // Une étape PAR LIGNE (retour du responsable pédagogique — style feuille)
    if (randomChoice([true, false])) {
        const t = tirerProgrammeEntier();
        const texte = `<p>Pars du nombre <strong>${t.n}</strong>. Multiplie-le par ${t.k}, ` +
            `ajoute ${t.p} au résultat, puis divise le tout par ${t.d}.</p><p>Complète :</p>`;
        const phrase =
            `${t.n} × ${t.k} = {{${t.nk}}}\n` +
            `{{${t.nk}}} + ${t.p} = {{${t.v}}}\n` +
            `{{${t.v}}} ÷ ${t.d} = {{${t.f}}}`;
        return makeTexteTrou(phrase, texte, { ...opts(L_PROG), mode: 'saisie' });
    }
    const t = tirerProgrammeDecimal();
    const xF = dixToFr(t.x10), yF = dixToFr(t.y10), sF = dixToFr(t.s10);
    const texte = `<p>Pars du nombre <strong>${xF}</strong>. Ajoute ${yF}, multiplie le résultat ` +
        `par 100, puis soustrais ${t.q}.</p><p>Complète :</p>`;
    const phrase =
        `${xF} + ${yF} = {{${sF}}}\n` +
        `{{${sF}}} × 100 = {{${t.v}}}\n` +
        `{{${t.v}}} − ${t.q} = {{${t.f}}}`;
    return makeTexteTrou(phrase, texte, { ...opts(L_PROG), mode: 'saisie' });
};

const genProgrammeQcm = () => {
    if (randomChoice([true, false])) {
        const t = tirerProgrammeEntier();
        const bonne = String(t.f);
        // Misconceptions : v (oublie la division), nk (s'arrête après la 1re étape)
        const distracteurs = dedupeChoix(bonne,
            [String(t.v), String(t.nk), String(t.f + 1)],
            [String(t.f - 1), String(t.f + 2)]);
        const texte = `<p>Pars du nombre <strong>${t.n}</strong>. Multiplie-le par ${t.k}, ` +
            `ajoute ${t.p} au résultat, puis divise le tout par ${t.d}.</p>` +
            '<p>Quel nombre obtiens-tu ?</p>';
        return makeQcm([bonne, ...distracteurs], bonne, texte, opts(L_PROG));
    }
    const t = tirerProgrammeDecimal();
    const xF = dixToFr(t.x10), yF = dixToFr(t.y10);
    const bonne = String(t.f);
    // Misconceptions : ×100 oublié (s − q peut être négatif → borné), s (s'arrête tôt)
    const distracteurs = dedupeChoix(bonne,
        [dixToFr(t.s10), String(t.v), String(t.f + 10)],
        [String(t.f - 10), String(t.f + 100)]);
    const texte = `<p>Pars du nombre <strong>${xF}</strong>. Ajoute ${yF}, multiplie le résultat ` +
        'par 100, puis soustrais ' + `${t.q}.</p><p>Quel nombre obtiens-tu ?</p>`;
    return makeQcm([bonne, ...distracteurs], bonne, texte, opts(L_PROG));
};

// ═══════════════════════════════════════════════════════════
// Carte 3 — Remettre les étapes dans l'ordre
// ═══════════════════════════════════════════════════════════

/**
 * Quatre chaînes STRICTES — chaque étape consomme le résultat de la précédente,
 * l'ordre est donc unique.
 *
 * A et B sont MONOTONES CROISSANTES : le premier nombre de chaque étiquette
 * grandit d'étape en étape. Mesuré à la revue (2026-08-02), cela suffisait à
 * réussir en rangeant simplement les étiquettes du plus petit nombre au plus
 * grand — 92,4 % de succès sans lire l'expression de départ (A2). C et D
 * cassent l'heuristique : la division y fait REDESCENDRE la valeur courante.
 * Elles font en outre entrer le ÷ dans la carte, absent des deux premières.
 */
const GABARITS_ORDRE = [
    () => {   // A = a − (b + c × d) — chaîne c×d → b+e → a−f (croissante)
        const c = randInt(2, 6), d = randInt(3, 9);
        const e = c * d;
        const b = randInt(3, 15);
        const f = b + e;
        const g = randInt(5, 30);
        const a = f + g;
        return {
            expr: `A = ${a} − (${b} + ${c} × ${d})`,
            etapes: [`${c} × ${d} = ${e}`, `${b} + ${e} = ${f}`, `${a} − ${f} = ${g}`],
        };
    },
    () => {   // B = (a + b) × c − d — chaîne a+b → s×c → m−d (croissante)
        const a = randInt(3, 15), b = randInt(2, 12);
        const s = a + b;
        const c = randInt(2, 6);
        const m = s * c;
        const d = randInt(2, m - 1 > 30 ? 30 : m - 1);
        return {
            expr: `B = (${a} + ${b}) × ${c} − ${d}`,
            etapes: [`${a} + ${b} = ${s}`, `${s} × ${c} = ${m}`, `${m} − ${d} = ${m - d}`],
        };
    },
    () => {   // C = (a − b) ÷ c + d — chaîne a−b → s÷c → q+d (DÉcroissante)
        const c = randInt(2, 6), q = randInt(3, 15);
        const s = q * c;                       // division exacte par construction
        const b = randInt(3, 20);
        const a = s + b;
        const d = randInt(2, 25);
        return {
            expr: `C = (${a} − ${b}) ÷ ${c} + ${d}`,
            etapes: [`${a} − ${b} = ${s}`, `${s} ÷ ${c} = ${q}`, `${q} + ${d} = ${q + d}`],
        };
    },
    () => {   // D = a − b × c ÷ d — chaîne b×c → m÷d → a−q (non monotone)
        for (let i = 0; i < 40; i++) {
            const b = randInt(2, 12), c = randInt(2, 12);
            const m = b * c;
            // ÷ exacte, et quotient assez petit pour que `a` reste sous `m`
            // d ∉ {b, c} : sinon on tombe sur « × 2 ÷ 2 », un calcul qui s'annule (A8).
            const diviseurs = [2, 3, 4, 5, 6].filter(
                (d) => m % d === 0 && m / d >= 3 && m / d <= 20 && d !== b && d !== c,
            );
            if (!diviseurs.length) continue;
            const d = randomChoice(diviseurs);
            const q = m / d;
            const g = randInt(4, 30);
            const a = q + g;
            return {
                expr: `D = ${a} − ${b} × ${c} ÷ ${d}`,
                etapes: [`${b} × ${c} = ${m}`, `${m} ÷ ${d} = ${q}`, `${a} − ${q} = ${g}`],
            };
        }
        return {   // repli : le cas 8 × 9 ÷ 6
            expr: 'D = 25 − 8 × 9 ÷ 6',
            etapes: ['8 × 9 = 72', '72 ÷ 6 = 12', '25 − 12 = 13'],
        };
    },
];

const PERMS_3 = [[0, 1, 2], [0, 2, 1], [1, 0, 2], [1, 2, 0], [2, 0, 1], [2, 1, 0]];

const operandes = (etape) => etape.split(' = ')[0].split(/ [+−×÷] /).map(s => s.trim());
const resultat = (etape) => etape.split(' = ')[1].trim();

/**
 * Garde-fou d'UNICITÉ (revue 2026-08-02). `makeOrdonner` corrige sur l'ordre
 * exact : si deux rangements différents se défendent, l'élève qui trouve le
 * second est compté faux (🔴). Un rangement est défendable quand :
 *   1. chaque étape consomme le résultat de la précédente ;
 *   2. la première n'utilise que des nombres écrits dans l'énoncé ;
 *   3. la dernière produit la valeur de l'expression.
 * Des collisions de valeurs à l'intérieur d'un tirage en ouvrent parfois un
 * deuxième (mesuré : 3,6 % des tirages du gabarit D, 0,3 % de C, 0 % de A et B).
 * On compte, et on retire tant qu'il n'y en a pas exactement un — plutôt qu'une
 * liste d'interdits qu'il faudrait rallonger à chaque gabarit ajouté.
 */
const ordresValides = (expr, etapes) => {
    const dispo = new Set(expr.match(/\d+(?:,\d+)?/g) || []);
    const valeur = resultat(etapes[etapes.length - 1]);
    return PERMS_3.filter((pm) => {
        const o = pm.map(i => etapes[i]);
        for (let i = 1; i < o.length; i++) {
            if (!operandes(o[i]).includes(resultat(o[i - 1]))) return false;
        }
        return operandes(o[0]).every(x => dispo.has(x)) && resultat(o[2]) === valeur;
    }).length;
};

const genEtapesOrdonner = () => {
    let tirage = randomChoice(GABARITS_ORDRE)();
    for (let i = 0; i < 40 && ordresValides(tirage.expr, tirage.etapes) !== 1; i++) {
        tirage = randomChoice(GABARITS_ORDRE)();
    }
    const texte = `<p>Remets dans l'ordre les étapes du calcul de <strong>${tirage.expr}</strong>.</p>`;
    return makeOrdonner(tirage.etapes, texte, opts(L_ORD));
};

// ═══════════════════════════════════════════════════════════
// Carte 4 — Vérifier les étapes (Léa)
// ═══════════════════════════════════════════════════════════

const PRENOMS_VER = ['Léa', 'Tom', 'Inès', 'Adam', 'Maya', 'Nour'];

/** B = (x − y) × k + p ÷ q — toutes les valeurs exactes par construction. */
const tirerVerifier = () => {
    const w = randInt(4, 12);                       // x − y (entier)
    let y10 = randInt(15, 95);
    if (y10 % 10 === 0) y10 += 5;
    const x10 = y10 + w * 10;
    const k = randInt(3, 6);
    const q = randInt(3, 5);
    const t = randInt(3, 8);
    const p = q * t;
    return {
        prenom: randomChoice(PRENOMS_VER),
        xF: dixToFr(x10), yF: dixToFr(y10),
        w, k, q, t, p,
    };
};

/**
 * V/F « Qui peut-on calculer, et quand ? » (retour du responsable pédagogique :
 * l'objectif est la PRIORITÉ OPÉRATOIRE, pas la détection d'erreurs de calcul).
 * Toutes les affirmations sont arithmétiquement JUSTES — seul l'ordre est en
 * jeu. Points clés : les deux branches (x−y) et p÷q sont INDÉPENDANTES (on
 * peut commencer par l'une ou l'autre) ; les voisins visuels k + p n'ont pas
 * le droit de s'additionner ; la dernière opération est l'addition.
 */
const genVerifierVF = () => {
    const t = tirerVerifier();
    const exprB = `(${t.xF} − ${t.yF}) × ${t.k} + ${t.p} ÷ ${t.q}`;
    const groupeCommencer = [
        { txt: `On peut commencer par calculer ${t.xF} − ${t.yF}`, v: true },   // parenthèses
        { txt: `On peut commencer par calculer ${t.p} ÷ ${t.q}`, v: true },     // branche indépendante
        { txt: `On peut commencer par calculer ${t.k} + ${t.p}`, v: false },    // faux découpage visuel
    ];
    const groupeOrdre = [
        { txt: `Il faut avoir calculé ${t.xF} − ${t.yF} avant de multiplier par ${t.k}`, v: true },
        { txt: `Il faut avoir calculé ${t.p} ÷ ${t.q} avant de calculer ${t.xF} − ${t.yF}`, v: false },
    ];
    const groupeDerniere = [
        { txt: 'La dernière opération effectuée est l\'addition', v: true },
        { txt: 'La dernière opération effectuée est la division', v: false },
        { txt: 'La dernière opération effectuée est la multiplication', v: false },
    ];
    // 2 lignes « commencer » + 1 de chaque autre groupe → 4 lignes, mix garanti.
    // Aucun groupe « résultat final » : la consigne annonce que tous les calculs
    // sont justes, une ligne « B = <faux> » la contredirait (revue 2026-08-02).
    let lignes;
    for (let i = 0; i < 30; i++) {
        lignes = [
            ...randomChoice([[0, 1], [0, 2], [1, 2]]).map(ix => groupeCommencer[ix]),
            randomChoice(groupeOrdre),
            randomChoice(groupeDerniere),
        ];
        const nbVrais = lignes.filter(l => l.v).length;
        if (nbVrais >= 1 && nbVrais < lignes.length) break;
    }
    const texte = `<p>${t.prenom} veut calculer <strong>B = ${exprB}</strong>. ` +
        'Pour chaque affirmation, indique si elle est vraie ou fausse. ' +
        '<em>(Tous les calculs sont justes : c\'est l\'ordre qui compte !)</em></p>';
    return makeVraiFaux(lignes.map(l => l.txt), lignes.map(l => (l.v ? 0 : 1)), texte, opts(L_VER));
};

/**
 * QCM « Quelle opération sera effectuée en DERNIER ? » — la structure de
 * l'expression TOURNE (4 gabarits sur les mêmes ingrédients) pour que la
 * bonne réponse ne soit pas toujours l'addition. Pont vers O6 (opérateur de
 * tête = nature de l'expression).
 */
const genVerifierDerniereQcm = () => {
    const t = tirerVerifier();
    const structures = [
        { expr: `(${t.xF} − ${t.yF}) × ${t.k} + ${t.p} ÷ ${t.q}`, bonne: "l'addition" },
        { expr: `(${t.xF} − ${t.yF}) × ${t.k} − ${t.p} ÷ ${t.q}`, bonne: 'la soustraction' },   // wk > t par bornes
        // Facteur À GAUCHE (revue 2026-08-02) : écrite « (…) × k », cette
        // structure plaçait le × en dernière position du texte, là même où se
        // trouve la bonne réponse — avec la structure ÷ finale, « le dernier
        // opérateur écrit » réussissait 51 % du temps au lieu de 25 % (A2).
        { expr: `${t.k} × (${t.xF} − ${t.yF} + ${t.p})`, bonne: 'la multiplication' },
        { expr: `((${t.xF} − ${t.yF}) × ${t.k} + ${t.p}) ÷ ${t.q}`, bonne: 'la division' },
    ];
    const s = randomChoice(structures);
    const choix = ["l'addition", 'la soustraction', 'la multiplication', 'la division'];
    const texte = `<p>${t.prenom} veut calculer <strong>B = ${s.expr}</strong>.</p>` +
        '<p>Quelle opération sera effectuée en <strong>dernier</strong> ?</p>';
    return makeQcm(choix, s.bonne, texte, opts(L_VER));
};

// ─── Registry & Export ───────────────────────────────────────

const GENERATORS = {
    effectuer_texte:              genEffectuerSaisie,
    effectuer_texte_trou_saisie:  genEffectuerEtapesTrous,
    programme_texte_trou_saisie:  genProgrammeTrous,
    programme_qcm_unique:         genProgrammeQcm,
    etapes_ordonner:              genEtapesOrdonner,
    verifier_tableau_vf:          genVerifierVF,
    verifier_qcm_unique:          genVerifierDerniereQcm,
};

export const CATEGORIES = [
    {
        id: 'effectuer', label: L_EFF,
        formatsList: [
            { key: 'effectuer_texte',             label: 'Saisie' },
            { key: 'effectuer_texte_trou_saisie', label: 'Saisie trous' },
        ],
    },
    {
        id: 'programme', label: L_PROG,
        formatsList: [
            { key: 'programme_texte_trou_saisie', label: 'Saisie trous' },
            { key: 'programme_qcm_unique',        label: 'QCM' },
        ],
    },
    {
        id: 'etapes', label: L_ORD,
        formatsList: [{ key: 'etapes_ordonner', label: 'Ordonner' }],
    },
    {
        id: 'verifier', label: L_VER,
        formatsList: [
            { key: 'verifier_tableau_vf', label: 'Vrai/Faux' },
            { key: 'verifier_qcm_unique', label: 'QCM' },
        ],
    },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(
            (k) => k.startsWith(typeExercice) || k.includes(typeExercice),
        );
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    if (!payload._meta.generatorKey) payload._meta.generatorKey = key;
    return payload;
};

export const _internals = {
    dixToFr, dedupeChoix,
    GABARITS_SIMPLES, GABARITS_ETAPES,
    tirerProgrammeEntier, tirerProgrammeDecimal, tirerVerifier,
};
